if (typeof ReadMoreDescription !== 'function') {
  class ReadMoreDescription extends HTMLElement {
    constructor() {
      super();
      this.isCollapsed = true;
      this.transitioning = false;
      this.initialized = false;
    }

    connectedCallback() {
      this.contentEl = this.querySelector('[data-js-read-more-content]');
      this.buttonEl = this.querySelector('[data-js-read-more-button]');
      this.buttonTextEl = this.querySelector('[data-js-read-more-text]');
      this.maxLines = parseInt(this.getAttribute('data-max-lines') || '4', 10);
      this.readMoreText = this.getAttribute('data-read-more') || 'Read More';
      this.readLessText = this.getAttribute('data-read-less') || 'Read Less';

      if (!this.contentEl || !this.buttonEl) return;

      if (!this.initialized) {
        this.buttonEl.addEventListener('click', () => this.toggleExpand());
        this.initialized = true;
      }

      // Initial height check
      this.checkHeight();
      requestAnimationFrame(() => this.checkHeight());
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.checkHeight());
      }

      // Handle ResizeObserver for responsive line wrapping and dynamic content
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.transitioning) {
            this.checkHeight();
          }
        });
        this.resizeObserver.observe(this.contentEl);
      } else {
        this._resizeHandler = this.debounce(() => {
          if (!this.transitioning) {
            this.checkHeight();
          }
        }, 150);
        window.addEventListener('resize', this._resizeHandler);
      }

      // If inside an accordion / toggle-tab, listen for open
      const parentToggle = this.closest('toggle-tab');
      if (parentToggle) {
        this._toggleHandler = () => {
          setTimeout(() => this.checkHeight(), 220);
        };
        parentToggle.addEventListener('click', this._toggleHandler);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
      }
      if (this._toggleHandler) {
        const parentToggle = this.closest('toggle-tab');
        if (parentToggle) {
          parentToggle.removeEventListener('click', this._toggleHandler);
        }
      }
    }

    getLineHeight() {
      const sampleEl = this.contentEl.querySelector('p, h1, h2, h3, h4, h5, h6, li, span') || this.contentEl;
      const computedStyle = window.getComputedStyle(sampleEl);
      let lineHeight = parseFloat(computedStyle.lineHeight);
      
      if (isNaN(lineHeight)) {
        const fontSize = parseFloat(computedStyle.fontSize) || 16;
        lineHeight = fontSize * 1.5;
      }
      
      return lineHeight;
    }

    calculateTargetHeight() {
      const lineHeight = this.getLineHeight();
      return Math.round(lineHeight * this.maxLines);
    }

    checkHeight() {
      if (this.transitioning || !this.contentEl || !this.buttonEl) return;

      const actualHeight = this.contentEl.scrollHeight;

      // If hidden (e.g. inside a closed accordion tab or display:none), height will be 0.
      if (actualHeight === 0 || (this.contentEl.offsetWidth === 0 && this.contentEl.offsetHeight === 0)) {
        return;
      }

      const targetHeight = this.calculateTargetHeight();
      
      // Buffer of 10px to prevent toggling for tiny rounding errors or padding
      const needsToggle = actualHeight > targetHeight + 10;

      if (!needsToggle) {
        this.buttonEl.setAttribute('hidden', '');
        this.contentEl.style.maxHeight = 'none';
        this.removeAttribute('data-collapsed');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        return;
      }

      this.buttonEl.removeAttribute('hidden');

      if (this.isCollapsed) {
        this.contentEl.style.maxHeight = `${targetHeight}px`;
        this.setAttribute('data-collapsed', 'true');
        this.buttonEl.setAttribute('aria-expanded', 'false');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readMoreText;
        }
      } else {
        this.setAttribute('data-collapsed', 'false');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readLessText;
        }
      }
    }

    toggleExpand() {
      if (this.transitioning || !this.contentEl || !this.buttonEl) return;

      const targetHeight = this.calculateTargetHeight();
      const fullHeight = this.contentEl.scrollHeight;

      this.transitioning = true;

      if (this.isCollapsed) {
        // EXPANDING
        this.isCollapsed = false;
        this.setAttribute('data-collapsed', 'false');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readLessText;
        }

        this.contentEl.style.maxHeight = `${fullHeight}px`;

        let transitionEnded = false;
        const onTransitionEnd = (e) => {
          if (e && e.target !== this.contentEl) return;
          if (transitionEnded) return;
          transitionEnded = true;
          this.contentEl.removeEventListener('transitionend', onTransitionEnd);
          if (!this.isCollapsed) {
            this.contentEl.style.maxHeight = 'none';
          }
          this.transitioning = false;
        };

        this.contentEl.addEventListener('transitionend', onTransitionEnd);
        setTimeout(onTransitionEnd, 450);

      } else {
        // COLLAPSING
        this.isCollapsed = true;
        
        // Lock to current pixel height first for smooth CSS transition
        this.contentEl.style.maxHeight = `${this.contentEl.getBoundingClientRect().height}px`;
        
        // Force reflow
        this.contentEl.offsetHeight;

        this.setAttribute('data-collapsed', 'true');
        this.buttonEl.setAttribute('aria-expanded', 'false');

        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readMoreText;
        }

        requestAnimationFrame(() => {
          this.contentEl.style.maxHeight = `${targetHeight}px`;
        });

        let transitionEnded = false;
        const onTransitionEnd = (e) => {
          if (e && e.target !== this.contentEl) return;
          if (transitionEnded) return;
          transitionEnded = true;
          this.contentEl.removeEventListener('transitionend', onTransitionEnd);
          this.transitioning = false;

          // If scrolled past the top of the description, gently scroll back to keep it in view
          const rect = this.getBoundingClientRect();
          if (rect.top < 0) {
            this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        };

        this.contentEl.addEventListener('transitionend', onTransitionEnd);
        setTimeout(onTransitionEnd, 450);
      }
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  if (typeof customElements.get('read-more-description') === 'undefined') {
    customElements.define('read-more-description', ReadMoreDescription);
  }
}
